import { create } from 'zustand';

import { StepUpChallenge, StepUpRepository } from '~/repositories/StepUpRepository';
import { useNotificationStore } from '~/stores/Notification';

type PendingStepUp = {
  promise: Promise<void>;
  resolve: () => void;
  reject: (reason?: unknown) => void;
};

// StepUpCancelledError signals the user dismissed the dialog; transports catch it and surface their
// original step_up_auth_required error instead.
export class StepUpCancelledError extends Error {
  constructor() {
    super('Step-up authentication cancelled');
    this.name = 'StepUpCancelledError';
  }
}

let pending: PendingStepUp | null = null;

const errorMessage = (error: unknown, fallback: string): string =>
  (typeof error === 'object' && error !== null && (error as { message?: string }).message) || fallback;

interface StepUpStoreState {
  challenge: StepUpChallenge | null;
  isVerifying: boolean;
  // Latched when the user dismisses the dialog so the interceptor stops auto-re-challenging a passive GET
  // that a data layer keeps retrying (otherwise cancelling loops forever). Cleared when a new challenge starts.
  declined: boolean;
  requireStepUp: () => Promise<void>;
  submit: (proof: { code?: string; password?: string }) => Promise<void>;
  resend: () => Promise<void>;
  cancel: () => void;
}

/**
 * Global step-up authentication state. Transports call requireStepUp() when a request is rejected with
 * step_up_auth_required; concurrent callers share one challenge, and every waiter is released when the
 * proof is recorded. StepUpDialogHost (mounted once at the app root) renders the dialog off this store.
 */
export const useStepUpStore = create<StepUpStoreState>((set, get) => ({
  challenge: null,
  isVerifying: false,
  declined: false,

  requireStepUp: () => {
    if (pending) {
      return pending.promise;
    }

    let resolve!: () => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    const current: PendingStepUp = { promise, resolve, reject };
    pending = current;
    // A fresh challenge is starting: clear any prior decline so this one is honored.
    set({ declined: false });

    // Detached so waiters get the promise (and cancellation) immediately, even while begin is in flight.
    void (async () => {
      try {
        const { challengeName } = await StepUpRepository.begin();
        // Cancelled while begin was in flight: the waiters are already rejected, don't reopen the dialog.
        if (pending === current) {
          set({ challenge: challengeName });
        }
      } catch (error) {
        if (pending === current) {
          pending = null;
          set({ challenge: null });
        }
        current.reject(error);
      }
    })();

    return promise;
  },

  submit: async (proof) => {
    const current = pending;
    if (!current || get().isVerifying) {
      return;
    }
    set({ isVerifying: true });
    try {
      await StepUpRepository.finish(proof);
    } catch (error) {
      if (pending === current) {
        useNotificationStore.getState().addDangerNotification({
          content: errorMessage(error, 'Failed to verify. Please try again.'),
          display: 'popup',
        });
        set({ isVerifying: false });
      }
      return;
    }

    // Cancelled (or superseded by a new challenge) while finish was in flight: the proof is recorded
    // server-side, but these waiters are already rejected — never resolve a different challenge.
    if (pending !== current) {
      return;
    }

    pending = null;
    set({ challenge: null, isVerifying: false });
    current.resolve();
  },

  resend: async () => {
    try {
      // begin re-derives the challenge and re-sends the SMS code.
      await StepUpRepository.begin();
      useNotificationStore.getState().addSuccessNotification({ content: 'Code resent', display: 'popup' });
    } catch (error) {
      useNotificationStore.getState().addDangerNotification({
        content: errorMessage(error, 'Failed to resend code.'),
        display: 'popup',
      });
    }
  },

  cancel: () => {
    const current = pending;
    pending = null;
    set({ challenge: null, isVerifying: false, declined: true });
    current?.reject(new StepUpCancelledError());
  },
}));
